import {
  JourneyEvent,
  Journey,
  AnnotationView,
  EditorMode,
  Product,
  AnnotationData,
  CartView,
  TimelineEvent,
  CameraMode,
} from "@/entities/annotation/model/types";
import {
  JourneyProximity,
  JourneyTransaction,
  Order,
  ProximityTimeRange,
  TrackPersistence,
  HandProjectionData,
} from "@/shared/api/lod-api";
import { PayloadAction, createSelector, createSlice } from "@reduxjs/toolkit";
import {
  ANNOTATION_TYPES,
  CART_VIEWS,
  EDITOR_MODES,
  ANNOTATION_RECORD_STATUSES,
  MAX_PRODUCT_QUANTITY_IN_ANY_UNIT,
  MIN_PRODUCT_COUNT,
  TIMELINE_EVENT_TYPES,
  CAMERA_MODES,
} from "../consts";
import { Gondola, SkuSection, WithIndex } from "@/shared/api";
import { allOption } from "@/features/annotation/ui/gondola-select";
import {
  findEventById,
  getStoredFavoriteCameras,
} from "@/features/annotation/lib/helpers";
import { RelativeCoordinate } from "@/shared/types";
import {
  PinBasedPrediction,
  ZoneBasedPrediction,
} from "@/features/annotation/lib/types";
import { getStoredTimelineRange } from "@/features/annotation/lib/helpers";
import { getDefaultCamera } from "@/features/annotation/lib/utils";

type AnnotationState = {
  view: AnnotationView;
  cartView: CartView;
  isComparisonModalVisible: boolean;
  currentCamera: string;
  isPlaybackLoaded: boolean;
  editorMode: EditorMode;
  isPlanogramViewVisible: boolean;
  isPlanogramSourceImageVisible: boolean;
  selectedJourneyId: string | null;
  orders: Order[];
  filteredOrders: Order[];
  selectedOrder: Order | null;
  isOrderLoading: boolean;
  isAnnotationSaving: boolean;
  journeys: Journey[];
  events: JourneyEvent[];
  journeyTransactions: JourneyTransaction[];
  trackPersistences: TrackPersistence[];
  journeyProximities: JourneyProximity[];
  proximityTimeRanges: ProximityTimeRange[];
  pinBasedPredictions: PinBasedPrediction[];
  zoneBasedPredictions: ZoneBasedPrediction[];
  selectedEvent:
    | JourneyEvent
    | JourneyTransaction
    | TrackPersistence
    | JourneyProximity
    | ProximityTimeRange
    | PinBasedPrediction
    | ZoneBasedPrediction
    | null;
  annotationData: AnnotationData | null;
  selectedProduct: Product | null;
  dateRange: {
    start: Date | null;
    end: Date | null;
  };
  videStartDate: Date | null;
  canvasDimensions: {
    width: number;
    height: number;
  };
  zoomLevel: number | null;
  seekAmount: string;
  timelineRange: {
    beforeMinutes: string;
    afterMinutes: string;
  };
  selectedSkuSectionId: number | null;
  gondolas: Gondola[];
  gondolaDropdownValue: Gondola | null;
  selectedGondola: Gondola | null;
  selectedCameraMode: CameraMode;
  selectedPolygon: WithIndex<SkuSection> | null;
  relatedCameraIds: string[];
  highlightedTimelineEventId: string | null;
  favoriteCameras: Record<string, string[]>;
  handProjections: {
    projections: HandProjectionData[];
    handId: string;
    trackId: number;
  } | null;
};

const initialState = (): AnnotationState => ({
  view: ANNOTATION_TYPES.JOURNEY,
  cartView: CART_VIEWS.ANNOTATED,
  isComparisonModalVisible: false,
  currentCamera: getDefaultCamera(),
  isPlaybackLoaded: false,
  editorMode: EDITOR_MODES.READ_ONLY,
  isPlanogramViewVisible: false,
  isPlanogramSourceImageVisible: false,
  selectedJourneyId: null,
  orders: [],
  filteredOrders: [],
  journeys: [],
  events: [],
  journeyTransactions: [],
  trackPersistences: [],
  journeyProximities: [],
  proximityTimeRanges: [],
  pinBasedPredictions: [],
  zoneBasedPredictions: [],
  dateRange: {
    start: null,
    end: null,
  },
  selectedOrder: null,
  isOrderLoading: false,
  isAnnotationSaving: false,
  selectedEvent: null,
  annotationData: null,
  selectedProduct: null,
  videStartDate: null,
  canvasDimensions: {
    width: 1920,
    height: 1080,
  },
  zoomLevel: localStorage.getItem("zoomLevel")
    ? parseFloat(localStorage.getItem("zoomLevel") ?? "0")
    : null,
  seekAmount: localStorage.getItem("seekAmount") || "1.000",
  timelineRange: getStoredTimelineRange(),
  selectedSkuSectionId: null,
  gondolas: [],
  gondolaDropdownValue: allOption,
  selectedGondola: null,
  selectedCameraMode: CAMERA_MODES.ALL,
  selectedPolygon: null,
  relatedCameraIds: [],
  highlightedTimelineEventId: null,
  favoriteCameras: getStoredFavoriteCameras(),
  handProjections: null,
});

export const annotationSlice = createSlice({
  name: "annotation",
  initialState,
  selectors: {
    selectAnnotationView: (state) => {
      return state.view;
    },
    selectCartView: (state) => {
      return state.cartView;
    },
    selectComparisonModalVisibility: (state) => {
      return state.isComparisonModalVisible;
    },
    selectCurrentCamera: (state) => {
      return state.currentCamera;
    },
    selectEditorMode: (state) => {
      return state.editorMode;
    },
    selectIsSkuSectionPlanogramVisible: (state) => {
      return state.isPlanogramViewVisible;
    },
    selectIsPlanogramSourceImageVisible: (state) => {
      return state.isPlanogramSourceImageVisible;
    },
    selectIsEditable: (state) => {
      return state.editorMode !== EDITOR_MODES.READ_ONLY;
    },
    selectCanvasDimensions: (state) => {
      return state.canvasDimensions;
    },
    selectEvents: (state) => {
      return state.events;
    },
    selectSelectedEvent: (state) => {
      return state.selectedEvent;
    },
    selectAnnotationData: (state) => {
      return state.annotationData;
    },
    selectDateRange: (state) => {
      return state.dateRange;
    },
    selectJourneys: (state) => {
      return state.journeys;
    },
    selectSelectedProduct: (state) => {
      return state.selectedProduct;
    },
    selectSelectedOrder: (state) => {
      return state.selectedOrder;
    },
    selectIsOrderLoading: (state) => {
      return state.isOrderLoading;
    },
    selectIsAnnotationSaving: (state) => {
      return state.isAnnotationSaving;
    },
    selectSelectedJourneyId: (state) => {
      return state.selectedJourneyId;
    },
    selectJourneyEvents: (state, journeyId: string) => {
      return state.events.filter((event) => event.journeyId === journeyId);
    },
    selectPreviousOrder: (state) => {
      const currentIndex = state.filteredOrders?.findIndex(
        (order) => order.id === state.selectedOrder?.id
      );

      return state.filteredOrders[currentIndex - 1];
    },
    selectNextOrder: (state) => {
      const currentIndex = state.filteredOrders.findIndex(
        (order) => order.id === state.selectedOrder?.id
      );

      return state.filteredOrders[currentIndex + 1];
    },
    selectSelectedOrderEvents: (state) =>
      state.events.filter((event) => event.orderId === state.selectedOrder?.id),
    selectSelectedJourney: (state) =>
      state.journeys.find((journey) => journey.id === state.selectedJourneyId),

    selectJourneyTransactions: (state) => {
      return state.journeyTransactions;
    },
    selectTrackPersistences: (state) => {
      return state.trackPersistences;
    },
    selectJourneyProximities: (state) => {
      return state.journeyProximities;
    },
    selectProximityTimeRanges: (state) => {
      return state.proximityTimeRanges;
    },
    selectPinBasedPredictions: (state) => {
      return state.pinBasedPredictions;
    },
    selectZoneBasedPredictions: (state) => {
      return state.zoneBasedPredictions;
    },
    selectSelectedSkuSectionId: (state) => {
      return state.selectedSkuSectionId;
    },
    selectGondolas: (state) => {
      return state.gondolas;
    },
    selectSelectedGondola: (state) => {
      return state.selectedGondola;
    },
    selectGondolaDropdownValue: (state) => {
      return state.gondolaDropdownValue;
    },
    selectSelectedCameraMode: (state) => {
      return state.selectedCameraMode;
    },
    selectSelectedPolygon: (state) => {
      return state.selectedPolygon;
    },
    selectRelatedCameraIds: (state) => {
      return state.relatedCameraIds;
    },
    selectHighlightedTimelineEventId: (state) => {
      return state.highlightedTimelineEventId;
    },
    selectIsPlaybackLoaded: (state) => {
      return state.isPlaybackLoaded;
    },
    selectTimelineRangeMinutes: (state) => {
      return state.timelineRange;
    },
    selectFavoriteCameras: (state) => state.favoriteCameras,
    selectStoreFavoriteCameras: (state, storeKey: string) =>
      state.favoriteCameras[storeKey] || [],
    selectHandProjections: (state) => state.handProjections,
  },
  reducers: {
    setAnnotationView(state, action: PayloadAction<AnnotationView>) {
      state.view = action.payload;
    },
    setCartView(state, action: PayloadAction<CartView>) {
      state.cartView = action.payload;
    },
    setComparisonModalVisibility(state, action: PayloadAction<boolean>) {
      state.isComparisonModalVisible = action.payload;
    },
    setCurrentCamera(state, action: PayloadAction<string | number>) {
      state.currentCamera = String(action.payload);
    },
    setOrders(state, action: PayloadAction<Order[]>) {
      state.orders = action.payload;
    },
    setFilteredOrders(state, action: PayloadAction<Order[] | null>) {
      state.filteredOrders = action.payload || [];
    },
    updateEditorMode(
      state,
      action: PayloadAction<AnnotationState["editorMode"]>
    ) {
      state.editorMode = action.payload;
    },
    setPlanogramViewVisibility: (state, action: PayloadAction<boolean>) => {
      state.isPlanogramViewVisible = action.payload;
    },
    setPlanogramOverlayVisibility: (state, action: PayloadAction<boolean>) => {
      state.isPlanogramSourceImageVisible = action.payload;
    },
    toggleSkuSectionPlanogram: (state) => {
      state.isPlanogramViewVisible = !state.isPlanogramViewVisible;
    },
    togglePlanogramOverlay: (state) => {
      state.isPlanogramSourceImageVisible =
        !state.isPlanogramSourceImageVisible;
    },
    addJourney(state, action: PayloadAction<string>) {
      state.journeys = [
        ...state.journeys,
        {
          id: action.payload,
          status: ANNOTATION_RECORD_STATUSES.NEW,
          note: "",
        },
      ];
    },
    setJourneys(state, action: PayloadAction<Journey[]>) {
      state.journeys = action.payload;
    },

    setSelectedJourneyId(
      state,
      action: PayloadAction<AnnotationState["selectedJourneyId"]>
    ) {
      state.selectedJourneyId = action.payload;
    },

    deleteJourney(state, action: PayloadAction<Journey>) {
      switch (action.payload.status) {
        case ANNOTATION_RECORD_STATUSES.NEW:
          state.events = state.events.filter(
            (event) => event.journeyId !== action.payload.id
          );
          state.journeys = state.journeys.filter(
            (journey) => journey.id !== action.payload.id
          );
          break;
        case ANNOTATION_RECORD_STATUSES.UNCHANGED:
        case ANNOTATION_RECORD_STATUSES.MODIFIED:
          state.journeys = state.journeys.map((journey) =>
            journey.id === action.payload.id
              ? { ...journey, status: ANNOTATION_RECORD_STATUSES.DELETED }
              : journey
          );
          break;
      }
    },

    setFirstJourneyIdAsSelected(state) {
      state.selectedJourneyId = state.journeys[0]?.id;
    },

    updateIsPlaybackLoaded(state, action: PayloadAction<boolean>) {
      state.isPlaybackLoaded = action.payload;
    },
    setAnnotationData(state, action: PayloadAction<AnnotationData | null>) {
      state.annotationData = action.payload;
    },
    captureAnnotationData(
      state,
      action: PayloadAction<{
        pinCoordinates: RelativeCoordinate;
        timestamp: string;
        calibrationId: number;
        planogramId: number;
        cameraImageWidth: number;
        cameraImageHeight: number;
      }>
    ) {
      const {
        selectedOrder,
        selectedJourneyId: journeyId,
        currentCamera: cameraId,
        selectedGondola,
      } = state;
      const {
        pinCoordinates,
        timestamp,
        calibrationId,
        planogramId,
        cameraImageWidth,
        cameraImageHeight,
      } = action.payload;

      if (!journeyId || !cameraId || !selectedOrder) {
        throw new Error("Required state values are missing");
      }

      state.annotationData = {
        orderId: selectedOrder.id,
        journeyId,
        cameraId,
        gondolaName: selectedGondola?.name || null,
        gondolaId: selectedGondola?.id || null,
        pinCoordinates,
        timestamp,
        calibrationId,
        planogramId,
        cameraImageWidth,
        cameraImageHeight,
      };
    },
    setSelectedProduct(state, action: PayloadAction<Product | null>) {
      state.selectedProduct = action.payload;
    },
    setSelectedProductCount(state, action: PayloadAction<number>) {
      if (state.selectedProduct) {
        state.selectedProduct.count = action.payload;
      }
    },
    increaseSelectedProductCount(state) {
      if (
        state.selectedProduct &&
        state.selectedProduct.count < MAX_PRODUCT_QUANTITY_IN_ANY_UNIT
      ) {
        state.selectedProduct.count += 1;
      }
    },
    decreaseSelectedProductCount(state) {
      if (
        state.selectedProduct &&
        state.selectedProduct.count > MIN_PRODUCT_COUNT
      ) {
        state.selectedProduct.count -= 1;
      }
    },
    setDateRange(state, action: PayloadAction<AnnotationState["dateRange"]>) {
      state.dateRange = action.payload;
    },
    updateVideoStartDate(state, action: PayloadAction<Date | null>) {
      state.videStartDate = action.payload;
    },

    updateCanvasDimensions(
      state,
      action: PayloadAction<AnnotationState["canvasDimensions"]>
    ) {
      state.canvasDimensions = action.payload;
    },
    setSelectedEvent(
      state,
      action: PayloadAction<AnnotationState["selectedEvent"]>
    ) {
      state.selectedEvent = action.payload;
    },
    setSelectedEventById(state, action: PayloadAction<string>) {
      const {
        events,
        journeyTransactions,
        trackPersistences,
        journeyProximities,
        proximityTimeRanges,
        pinBasedPredictions,
        zoneBasedPredictions,
      } = state;

      const foundEvent = findEventById(
        {
          events,
          journeyTransactions,
          trackPersistences,
          journeyProximities,
          proximityTimeRanges,
          pinBasedPredictions,
          zoneBasedPredictions,
        },
        action.payload
      );
      state.selectedEvent = foundEvent;
    },
    setSelectedOrder(
      state,
      action: PayloadAction<AnnotationState["selectedOrder"]>
    ) {
      state.selectedOrder = action.payload;
    },
    setOrderLoading(state, action: PayloadAction<boolean>) {
      state.isOrderLoading = action.payload;
    },
    setAnnotationSaving(state, action: PayloadAction<boolean>) {
      state.isAnnotationSaving = action.payload;
    },
    addJourneyEvent(state, action: PayloadAction<JourneyEvent>) {
      const selectedJourney = state.journeys.find(
        (journey) => journey.id === action.payload.journeyId
      );

      if (selectedJourney?.status === ANNOTATION_RECORD_STATUSES.UNCHANGED) {
        state.journeys = state.journeys.map((journey) =>
          journey.id === action.payload.journeyId
            ? { ...journey, status: ANNOTATION_RECORD_STATUSES.MODIFIED }
            : journey
        );
      }

      state.events.push(action.payload);
    },
    addJourneyEvents(state, action: PayloadAction<JourneyEvent[]>) {
      const events = action.payload;
      if (!events.length) return;

      const eventsByJourneyId = events.reduce(
        (acc, event) => {
          if (!acc[event.journeyId]) {
            acc[event.journeyId] = [];
          }
          acc[event.journeyId].push(event);
          return acc;
        },
        {} as Record<string, JourneyEvent[]>
      );

      const journeyIdsToUpdate = Object.keys(eventsByJourneyId).filter(
        (journeyId) => {
          const journey = state.journeys.find((j) => j.id === journeyId);
          return journey?.status === ANNOTATION_RECORD_STATUSES.UNCHANGED;
        }
      );

      if (journeyIdsToUpdate.length > 0) {
        state.journeys = state.journeys.map((journey) =>
          journeyIdsToUpdate.includes(journey.id)
            ? { ...journey, status: ANNOTATION_RECORD_STATUSES.MODIFIED }
            : journey
        );
      }

      state.events = events;
    },
    deleteJourneyEvent(state, action: PayloadAction<{ type: string }>) {
      state.events = state.events.filter(
        (event) =>
          !(
            event.journeyId === state.selectedJourneyId &&
            event.eventType === action.payload.type
          )
      );
    },
    setJourneyTransactions(state, action: PayloadAction<JourneyTransaction[]>) {
      state.journeyTransactions = action.payload;
    },
    setTrackPersistences(state, action: PayloadAction<TrackPersistence[]>) {
      state.trackPersistences = action.payload;
    },
    setJourneyProximities(state, action: PayloadAction<JourneyProximity[]>) {
      state.journeyProximities = action.payload;
    },
    setProximityTimeRanges(state, action: PayloadAction<ProximityTimeRange[]>) {
      state.proximityTimeRanges = action.payload;
    },
    setPinBasedPredictions(state, action: PayloadAction<PinBasedPrediction[]>) {
      state.pinBasedPredictions = action.payload;
    },
    setZoneBasedPredictions(
      state,
      action: PayloadAction<ZoneBasedPrediction[]>
    ) {
      state.zoneBasedPredictions = action.payload;
    },
    addJourneyTransaction(
      state,
      action: PayloadAction<Partial<JourneyTransaction>>
    ) {
      state.journeyTransactions = [
        ...state.journeyTransactions,
        {
          ...action.payload,
          type: ANNOTATION_TYPES.TRANSACTION,
          status: ANNOTATION_RECORD_STATUSES.NEW,
        } as JourneyTransaction,
      ];
    },
    addTrackPersistence(
      state,
      action: PayloadAction<Partial<TrackPersistence>>
    ) {
      state.trackPersistences = [
        ...state.trackPersistences,
        {
          ...action.payload,
          type: ANNOTATION_TYPES.TRACK_PERSISTENCE,
          status: ANNOTATION_RECORD_STATUSES.NEW,
        } as TrackPersistence,
      ];
    },
    addJourneyProximity(state, action: PayloadAction<JourneyProximity>) {
      state.journeyProximities.push(action.payload);
    },
    addProximityTimeRange(state, action: PayloadAction<ProximityTimeRange>) {
      state.proximityTimeRanges.push(action.payload);
    },
    deleteJourneyTransaction(state, action: PayloadAction<string>) {
      const event = state.journeyTransactions.find(
        ({ id }) => id === action.payload
      );

      switch (event?.status) {
        case ANNOTATION_RECORD_STATUSES.NEW:
          state.journeyTransactions = state.journeyTransactions.filter(
            (transaction) => transaction.id !== event.id
          );
          break;

        case ANNOTATION_RECORD_STATUSES.UNCHANGED:
          state.journeyTransactions = state.journeyTransactions.map(
            (transaction) =>
              transaction.id === event.id
                ? { ...transaction, status: ANNOTATION_RECORD_STATUSES.DELETED }
                : transaction
          );
          break;
      }
    },
    deleteTrackPersistence(state, action: PayloadAction<string>) {
      const event = state.trackPersistences.find(
        ({ id }) => id === action.payload
      );

      switch (event?.status) {
        case ANNOTATION_RECORD_STATUSES.NEW:
          state.trackPersistences = state.trackPersistences.filter(
            (trackPersistence) => trackPersistence.id !== event.id
          );
          break;

        case ANNOTATION_RECORD_STATUSES.UNCHANGED:
          state.trackPersistences = state.trackPersistences.map(
            (trackPersistence) =>
              trackPersistence.id === event.id
                ? {
                    ...trackPersistence,
                    status: ANNOTATION_RECORD_STATUSES.DELETED,
                  }
                : trackPersistence
          );
          break;
      }
    },
    deleteJourneyProximity(state, action: PayloadAction<string>) {
      const event = state.journeyProximities.find(
        ({ id }) => id === action.payload
      );

      switch (event?.status) {
        case ANNOTATION_RECORD_STATUSES.NEW:
          state.journeyProximities = state.journeyProximities.filter(
            (journeyProximity) => journeyProximity.id !== event.id
          );
          break;

        case ANNOTATION_RECORD_STATUSES.UNCHANGED:
          state.journeyProximities = state.journeyProximities.map(
            (journeyProximity) =>
              journeyProximity.id === event.id
                ? {
                    ...journeyProximity,
                    status: ANNOTATION_RECORD_STATUSES.DELETED,
                  }
                : journeyProximity
          );
          break;
      }
    },
    deleteProximityTimeRange(state, action: PayloadAction<string>) {
      const event = state.proximityTimeRanges.find(
        ({ id }) => id === action.payload
      );

      switch (event?.status) {
        case ANNOTATION_RECORD_STATUSES.NEW:
          state.proximityTimeRanges = state.proximityTimeRanges.filter(
            (proximityTimeRange) => proximityTimeRange.id !== event.id
          );
          break;

        case ANNOTATION_RECORD_STATUSES.UNCHANGED:
          state.proximityTimeRanges = state.proximityTimeRanges.map(
            (proximityTimeRange) =>
              proximityTimeRange.id === event.id
                ? {
                    ...proximityTimeRange,
                    status: ANNOTATION_RECORD_STATUSES.DELETED,
                  }
                : proximityTimeRange
          );
          break;
      }
    },
    cancelAnnotation(state) {
      state.isPlanogramViewVisible = false;
      state.isPlanogramSourceImageVisible = false;
      state.editorMode = EDITOR_MODES.READ_ONLY;
      state.selectedJourneyId = null;
      state.journeys = [];
      state.events = [];
      state.journeyTransactions = [];
      state.trackPersistences = [];
      state.journeyProximities = [];
      state.proximityTimeRanges = [];
      state.selectedEvent = null;
      state.annotationData = null;
      state.selectedProduct = null;
      state.selectedSkuSectionId = null;
      state.selectedPolygon = null;
    },
    clearEventList(state) {
      state.events = [];
    },
    updateZoomLevel(
      state,
      action: PayloadAction<AnnotationState["zoomLevel"]>
    ) {
      state.zoomLevel = action.payload;
    },
    updateSeekAmount(
      state,
      action: PayloadAction<AnnotationState["seekAmount"]>
    ) {
      state.seekAmount = action.payload;
    },
    updateTimelineRangeMinutes(
      state,
      action: PayloadAction<{
        beforeMinutes: string;
        afterMinutes: string;
      }>
    ) {
      state.timelineRange = action.payload;
    },
    setSelectedSkuSectionId(state, action: PayloadAction<number | null>) {
      state.selectedSkuSectionId = action.payload;
    },
    setGondolas(state, action: PayloadAction<Gondola[] | null>) {
      state.gondolas = action.payload || [];
    },
    setSelectedGondola(state, action: PayloadAction<Gondola | null>) {
      state.selectedGondola = action.payload;
    },
    setGondolaByName(state, action: PayloadAction<string | null>) {
      const gondola = state.gondolas?.find(
        (gondola) => gondola.name === action.payload
      );

      state.selectedGondola = gondola || null;
    },
    setGondolaDropdownValue(state, action: PayloadAction<Gondola | null>) {
      state.gondolaDropdownValue = action.payload;
    },
    setGondolaDropdownValueByName(state, action: PayloadAction<string | null>) {
      const gondola = state.gondolas?.find(
        (gondola) => gondola.name === action.payload
      );
      if (gondola) {
        state.gondolaDropdownValue = gondola;
      }
    },
    setSelectedCameraMode(state, action: PayloadAction<CameraMode>) {
      state.selectedCameraMode = action.payload;
    },
    setSelectedPolygon(
      state,
      action: PayloadAction<WithIndex<SkuSection> | null>
    ) {
      state.selectedPolygon = action.payload;
    },
    setRelatedCameraIds(state, action: PayloadAction<string[]>) {
      state.relatedCameraIds = action.payload;
    },
    setHighlightedTimelineEventId(state, action: PayloadAction<string | null>) {
      state.highlightedTimelineEventId = action.payload;
    },
    addFavoriteCamera(
      state,
      action: PayloadAction<{ storeKey: string; cameraId: string }>
    ) {
      const { storeKey, cameraId } = action.payload;

      if (!storeKey || !cameraId) {
        return;
      }

      if (!state.favoriteCameras[storeKey]) {
        state.favoriteCameras[storeKey] = [];
      }

      const cameras = state.favoriteCameras[storeKey];
      if (!cameras.includes(cameraId)) {
        cameras.push(cameraId);
      }
    },
    removeFavoriteCamera(
      state,
      action: PayloadAction<{ storeKey: string; cameraId: string }>
    ) {
      const { storeKey, cameraId } = action.payload;

      if (!storeKey || !state.favoriteCameras[storeKey]) {
        return;
      }

      state.favoriteCameras[storeKey] = state.favoriteCameras[storeKey]?.filter(
        (id) => id !== cameraId
      );

      if (state.favoriteCameras[storeKey].length === 0) {
        delete state.favoriteCameras[storeKey];
      }
    },
    setHandProjections(
      state,
      action: PayloadAction<{
        projections: HandProjectionData[];
        handId: string;
        trackId: number;
      } | null>
    ) {
      state.handProjections = action.payload || null;
    },
    resetState(state) {
      const canvasDimensions = { ...state.canvasDimensions };
      Object.assign(state, initialState());
      state.canvasDimensions = canvasDimensions;
    },
  },
});

export const {
  setAnnotationView,
  setCartView,
  setComparisonModalVisibility,
  setCurrentCamera,
  updateIsPlaybackLoaded,
  updateVideoStartDate,
  setDateRange,
  updateCanvasDimensions,
  addJourney,
  deleteJourney,
  addJourneyEvent,
  addJourneyEvents,
  deleteJourneyEvent,
  clearEventList,
  setJourneyTransactions,
  addJourneyTransaction,
  deleteJourneyTransaction,
  setTrackPersistences,
  addTrackPersistence,
  deleteTrackPersistence,
  setJourneyProximities,
  setProximityTimeRanges,
  setPinBasedPredictions,
  setZoneBasedPredictions,
  addJourneyProximity,
  addProximityTimeRange,
  deleteJourneyProximity,
  deleteProximityTimeRange,
  setSelectedEvent,
  setSelectedEventById,
  setOrders,
  setFilteredOrders,
  setSelectedOrder,
  setOrderLoading,
  setAnnotationSaving,
  setAnnotationData,
  captureAnnotationData,
  setSelectedProduct,
  setSelectedProductCount,
  increaseSelectedProductCount,
  decreaseSelectedProductCount,
  cancelAnnotation,
  resetState,
  updateEditorMode,
  setPlanogramViewVisibility,
  toggleSkuSectionPlanogram,
  setPlanogramOverlayVisibility,
  togglePlanogramOverlay,
  setJourneys,
  setSelectedJourneyId,
  setFirstJourneyIdAsSelected,
  updateZoomLevel,
  updateSeekAmount,
  updateTimelineRangeMinutes,
  setSelectedSkuSectionId,
  setGondolas,
  setSelectedGondola,
  setSelectedCameraMode,
  setGondolaByName,
  setGondolaDropdownValue,
  setGondolaDropdownValueByName,
  setSelectedPolygon,
  setRelatedCameraIds,
  setHighlightedTimelineEventId,
  addFavoriteCamera,
  removeFavoriteCamera,
  setHandProjections,
} = annotationSlice.actions;

export const {
  selectAnnotationView,
  selectCartView,
  selectComparisonModalVisibility,
  selectEditorMode,
  selectIsSkuSectionPlanogramVisible,
  selectIsPlanogramSourceImageVisible,
  selectIsEditable,
  selectCurrentCamera,
  selectCanvasDimensions,
  selectSelectedOrder,
  selectIsOrderLoading,
  selectIsAnnotationSaving,
  selectPreviousOrder,
  selectNextOrder,
  selectSelectedOrderEvents,
  selectSelectedJourney,
  selectSelectedJourneyId,
  selectJourneys,
  selectJourneyEvents,
  selectJourneyTransactions,
  selectTrackPersistences,
  selectJourneyProximities,
  selectProximityTimeRanges,
  selectPinBasedPredictions,
  selectZoneBasedPredictions,
  selectEvents,
  selectSelectedEvent,
  selectAnnotationData,
  selectDateRange,
  selectSelectedProduct,
  selectSelectedSkuSectionId,
  selectGondolas,
  selectSelectedGondola,
  selectGondolaDropdownValue,
  selectSelectedCameraMode,
  selectSelectedPolygon,
  selectRelatedCameraIds,
  selectHighlightedTimelineEventId,
  selectIsPlaybackLoaded,
  selectTimelineRangeMinutes,
  selectFavoriteCameras,
  selectStoreFavoriteCameras,
  selectHandProjections,
} = annotationSlice.selectors;

export const selectActiveJourneys = createSelector(
  [selectJourneys],
  (journeys) =>
    journeys.filter(
      (journey) => journey.status !== ANNOTATION_RECORD_STATUSES.DELETED
    )
);

export const selectActiveJourneyEvents = createSelector(
  [selectEvents, selectSelectedJourneyId],
  (journeyEvents, selectedJourneyId) =>
    journeyEvents.filter((event) => event.journeyId === selectedJourneyId)
);

export const selectActiveJourneyTransactions = createSelector(
  [selectJourneyTransactions, selectSelectedJourneyId],
  (journeyTransactions, selectedJourneyId) =>
    journeyTransactions.filter(
      (event) =>
        event.journeyId === selectedJourneyId &&
        event.status !== ANNOTATION_RECORD_STATUSES.DELETED
    )
);

export const selectActiveTrackPersistences = createSelector(
  [selectTrackPersistences, selectSelectedJourneyId],
  (trackPersistences, selectedJourneyId) =>
    trackPersistences.filter(
      (event) =>
        event.journeyId === selectedJourneyId &&
        event.status !== ANNOTATION_RECORD_STATUSES.DELETED
    )
);

export const selectActiveJourneyProximities = createSelector(
  [selectJourneyProximities, selectSelectedJourneyId],
  (journeyProximities, selectedJourneyId) =>
    journeyProximities.filter(
      (event) =>
        event.journeyId === selectedJourneyId &&
        event.status !== ANNOTATION_RECORD_STATUSES.DELETED
    )
);

export const selectActiveProximityTimeRanges = createSelector(
  [selectProximityTimeRanges, selectSelectedJourneyId],
  (proximityTimeRanges, selectedJourneyId) =>
    proximityTimeRanges.filter(
      (event) =>
        event.journeyId === selectedJourneyId &&
        event.status !== ANNOTATION_RECORD_STATUSES.DELETED
    )
);

export const selectAnnotatedCartItems = createSelector(
  [selectJourneyTransactions],
  (journeyTransactions) =>
    journeyTransactions.filter(
      (event) => event.status !== ANNOTATION_RECORD_STATUSES.DELETED
    )
);

export const selectSelectedJourneyEvents = createSelector(
  [selectEvents, selectSelectedJourneyId],
  (events, selectedJourneyId) =>
    events.filter((event) => event.journeyId === selectedJourneyId)
);

export const selectOrderTimelineEvents = createSelector(
  [
    selectSelectedOrder,
    selectEvents,
    selectJourneyTransactions,
    selectTrackPersistences,
    selectJourneyProximities,
    selectProximityTimeRanges,
    selectPinBasedPredictions,
    selectZoneBasedPredictions,
  ],
  (
    selectedOrder: Order | null,
    events: JourneyEvent[],
    journeyTransactions: JourneyTransaction[],
    trackPersistences: TrackPersistence[],
    journeyProximities: JourneyProximity[],
    proximityTimeRanges: ProximityTimeRange[],
    pinBasedPredictions: PinBasedPrediction[],
    zoneBasedPredictions: ZoneBasedPrediction[]
  ): TimelineEvent[] => {
    const filteredJourneyTransactions = journeyTransactions.filter(
      (transaction) => transaction.status !== ANNOTATION_RECORD_STATUSES.DELETED
    );

    const filteredTrackPersistences = trackPersistences.filter(
      (trackPersistence) =>
        trackPersistence.status !== ANNOTATION_RECORD_STATUSES.DELETED
    );

    const filteredJourneyProximities = journeyProximities.filter(
      (journeyProximity) =>
        journeyProximity.status !== ANNOTATION_RECORD_STATUSES.DELETED
    );

    const filteredProximityTimeRanges = proximityTimeRanges.filter(
      (proximityTimeRange) =>
        proximityTimeRange.status !== ANNOTATION_RECORD_STATUSES.DELETED
    );

    const eventItems: TimelineEvent[] = events.map((item) => ({
      id: item.id,
      type: TIMELINE_EVENT_TYPES.EVENT,
      timestamp: new Date(item.timestamp).toISOString(),
      cameraId: item.cameraId || null,
      gondolaName: null,
      journeyId: item.journeyId,
    }));

    const transactionItems: TimelineEvent[] = filteredJourneyTransactions.map(
      (item) => ({
        id: item.id,
        type: TIMELINE_EVENT_TYPES.TRANSACTION,
        timestamp: new Date(item.timestamp).toISOString(),
        cameraId: String(item.cameraId) || null,
        gondolaName: item.gondolaName,
        journeyId: item.journeyId,
      })
    );

    const trackPersistenceItems: TimelineEvent[] =
      filteredTrackPersistences.map((item) => ({
        id: item.id,
        type: TIMELINE_EVENT_TYPES.TRACK_PERSISTENCE,
        timestamp: new Date(item.timestamp).toISOString(),
        cameraId: String(item.cameraId) || null,
        gondolaName: null,
        journeyId: item.journeyId,
      }));

    const journeyProximityItems: TimelineEvent[] =
      filteredJourneyProximities.map((item) => ({
        id: item.id,
        type: TIMELINE_EVENT_TYPES.PROXIMITY,
        timestamp: new Date(item.timestamp).toISOString(),
        cameraId: String(item.cameraId) || null,
        gondolaName: item.gondolaName,
        journeyId: item.journeyId,
      }));

    const proximityTimeRangeItems: TimelineEvent[] =
      filteredProximityTimeRanges.flatMap((item) => {
        const timestampFields: Array<{
          field: keyof ProximityTimeRange;
          suffix?: string;
        }> = [
          { field: "timestamp" },
          { field: "beforeRangeStartTime", suffix: "before-start" },
          { field: "beforeRangeEndTime", suffix: "before-end" },
          { field: "afterRangeStartTime", suffix: "after-start" },
          { field: "afterRangeEndTime", suffix: "after-end" },
        ];

        return timestampFields.map(({ field, suffix }) => ({
          id: suffix ? `${item.id}_${suffix}` : item.id,
          type: TIMELINE_EVENT_TYPES.PROXIMITY_TIME_RANGES,
          timestamp: new Date(item[field] as string).toISOString(),
          cameraId: String(item.cameraId) || null,
          gondolaName: item.gondolaName,
          journeyId: item.journeyId,
        }));
      });

    const pinBasedPredictionItems: TimelineEvent[] = pinBasedPredictions.map(
      (item) => ({
        id: item.id,
        type: TIMELINE_EVENT_TYPES.PIN_BASED_PREDICTION,
        timestamp: new Date(item.timestamp).toISOString(),
        cameraId: item.cameraId ? String(item.cameraId) : null,
        gondolaName: item.gondolaName || null,
        journeyId: item.journeyId,
      })
    );

    const zoneBasedPredictionItems: TimelineEvent[] = zoneBasedPredictions.map(
      (item) => ({
        id: item.id,
        type: TIMELINE_EVENT_TYPES.ZONE_BASED_PREDICTION,
        timestamp: new Date(item.timestamp).toISOString(),
        cameraId: item.cameraId ? String(item.cameraId) : null,
        gondolaName: item.gondolaName || null,
        journeyId: null,
      })
    );

    const orderItem: TimelineEvent[] = selectedOrder
      ? [
          {
            id: selectedOrder.id,
            type: "order",
            timestamp: new Date(selectedOrder.time).toISOString(),
            cameraId: null,
            gondolaName: null,
            journeyId: null,
          },
        ]
      : [];

    return [
      ...eventItems,
      ...transactionItems,
      ...trackPersistenceItems,
      ...journeyProximityItems,
      ...proximityTimeRangeItems,
      ...pinBasedPredictionItems,
      ...zoneBasedPredictionItems,
      ...orderItem,
    ];
  }
);

export const selectSelectedJourneyCameraEvents = createSelector(
  [
    selectSelectedJourneyId,
    selectEvents,
    selectJourneyTransactions,
    selectTrackPersistences,
    selectJourneyProximities,
    selectProximityTimeRanges,
    selectPinBasedPredictions,
    selectZoneBasedPredictions,
    (_, cameraId: string) => cameraId,
  ],
  (
    selectedJourneyId,
    events,
    journeyTransactions,
    trackPersistences,
    journeyProximities,
    proximityTimeRanges,
    pinBasedPredictions,
    zoneBasedPredictions,
    cameraId
  ) => {
    if (!cameraId) {
      return [];
    }

    const journeyEvents = events.filter(
      (event) =>
        event.journeyId === selectedJourneyId && event.cameraId === cameraId
    );

    const filteredTransactions = journeyTransactions.filter(
      (event) =>
        event.journeyId === selectedJourneyId &&
        event.status !== ANNOTATION_RECORD_STATUSES.DELETED &&
        String(event.cameraId) === cameraId
    );

    const filteredTrackPersistences = trackPersistences.filter(
      (event) =>
        event.journeyId === selectedJourneyId &&
        event.status !== ANNOTATION_RECORD_STATUSES.DELETED &&
        String(event.cameraId) === cameraId
    );

    const filteredJourneyProximities = journeyProximities.filter(
      (event) =>
        event.journeyId === selectedJourneyId &&
        event.status !== ANNOTATION_RECORD_STATUSES.DELETED &&
        String(event.cameraId) === cameraId
    );

    const filteredProximityTimeRanges = proximityTimeRanges.filter(
      (event) =>
        event.journeyId === selectedJourneyId &&
        event.status !== ANNOTATION_RECORD_STATUSES.DELETED &&
        String(event.cameraId) === cameraId
    );

    const filteredPinBasedPredictions = pinBasedPredictions.filter(
      (event) =>
        event.journeyId === selectedJourneyId &&
        String(event.cameraId) === cameraId &&
        event.pinCoordinates
    );

    const filteredZoneBasedPredictions = zoneBasedPredictions.filter(
      (event) => String(event.cameraId) === cameraId && event.pinCoordinates
    );

    return [
      ...journeyEvents,
      ...filteredTransactions,
      ...filteredTrackPersistences,
      ...filteredJourneyProximities,
      ...filteredProximityTimeRanges,
      ...filteredPinBasedPredictions,
      ...filteredZoneBasedPredictions,
    ];
  }
);
